/**
 * Unified Status Mapper for Scraping Progress
 * 
 * Maps backend status strings to consistent frontend UI labels
 * Used across all progress components for uniform display
 */

import {
    faGlobe,
    faDatabase,
    faPalette,
    faWandMagicSparkles,
    faCheck,
    faSpinner,
    faExclamationTriangle,
    IconDefinition
} from '@fortawesome/free-solid-svg-icons';

export interface StageInfo {
    step: number;
    label: string;
    color: 'blue' | 'purple' | 'indigo' | 'violet' | 'green' | 'gray' | 'red' | 'yellow';
    icon: IconDefinition;
    description?: string;
}

// REVAMP: Minimal interface aligned with backend essential fields
export interface ProgressStatus {
    client_id?: string;
    status?: string;
    phase?: string;
    message?: string;
    progress?: number;  // Single progress field, 0-100
    
    // Scraping phase
    chunks_completed?: number;
    chunks_total?: number;
    pages_scraped?: number;
    total_pages?: number;
    
    // Embedding phase
    embedding_current?: number;
    embedding_total?: number;
    
    // Errors
    pages_failed?: number;
    errors_count?: number;
    
    // Timing
    start_time?: string;
    last_update?: string;
}

/**
 * REVAMP: Map backend phase to frontend stage information
 * Uses new phase names: batch_scraping, generating_embeddings, extracting_branding, applying_themes
 */
export function getStageInfo(status?: string, phase?: string): StageInfo {
    const normalizedPhase = (phase || '').toLowerCase();
    
    // Map phase directly (more reliable than status)
    const stages: Record<string, StageInfo> = {
        'batch_scraping': {
            step: 1,
            label: 'Scraping',
            color: 'blue',
            icon: faGlobe,
            description: 'Scraping website pages'
        },
        'batch_initializing': {
            step: 1,
            label: 'Scraping',
            color: 'blue',
            icon: faGlobe,
            description: 'Initializing scraping...'
        },
        'processing_results': {
            step: 2,
            label: 'Processing',
            color: 'purple',
            icon: faDatabase,
            description: 'Processing scraped content'
        },
        'generating_embeddings': {
            step: 3,
            label: 'Embeddings',
            color: 'purple',
            icon: faDatabase,
            description: 'Generating embeddings'
        },
        'awaiting_embeddings': {
            step: 3,
            label: 'Embeddings',
            color: 'purple',
            icon: faDatabase,
            description: 'Preparing embeddings'
        },
        'extracting_branding': {
            step: 4,
            label: 'Branding',
            color: 'indigo',
            icon: faPalette,
            description: 'Extracting brand colors'
        },
        'applying_themes': {
            step: 5,
            label: 'Themes',
            color: 'violet',
            icon: faWandMagicSparkles,
            description: 'Applying theme'
        },
        'completed': {
            step: 6,
            label: 'Complete',
            color: 'green',
            icon: faCheck,
            description: 'Scraping completed!'
        }
    };
    
    // Check for exact phase match first
    if (normalizedPhase && stages[normalizedPhase]) {
        return stages[normalizedPhase];
    }
    
    // Fallback to status-based matching
    const normalizedStatus = (status || '').toLowerCase();
    
    if (normalizedStatus === 'failed' || normalizedStatus === 'error') {
        return {
            step: -1,
            label: 'Failed',
            color: 'red',
            icon: faExclamationTriangle,
            description: 'Scraping failed'
        };
    }
    
    if (normalizedStatus === 'completed') {
        return stages['completed'];
    }
    
    // Default initializing state
    return {
        step: 0,
        label: 'Initializing',
        color: 'gray',
        icon: faSpinner,
        description: 'Starting...'
    };
}

/**
 * REVAMP: Get all progress steps for UI display
 * Based on new phase names
 */
export function getProgressSteps(currentStatus: ProgressStatus): Array<{
    key: string;
    label: string;
    icon: IconDefinition;
    active: boolean;
    complete: boolean;
}> {
    const phase = currentStatus.phase || '';
    const progress = currentStatus.progress || 0;
    
    return [
        {
            key: 'scraping',
            label: 'Scraping Website',
            icon: faGlobe,
            active: phase === 'batch_scraping' || phase === 'batch_initializing',
            complete: progress > 60,
        },
        {
            key: 'processing',
            label: 'Processing Results',
            icon: faDatabase,
            active: phase === 'processing_results',
            complete: progress > 60,
        },
        {
            key: 'embedding',
            label: 'Generating Embeddings',
            icon: faDatabase,
            active: phase === 'generating_embeddings' || phase === 'awaiting_embeddings',
            complete: progress > 85,
        },
        {
            key: 'branding',
            label: 'Extracting Branding',
            icon: faPalette,
            active: phase === 'extracting_branding',
            complete: progress > 95,
        },
        {
            key: 'themes',
            label: 'Applying Theme',
            icon: faWandMagicSparkles,
            active: phase === 'applying_themes',
            complete: progress >= 100,
        },
    ];
}

/**
 * Check if status indicates completion
 */
export function isCompleted(status?: string): boolean {
    const normalized = (status || '').toLowerCase();
    return normalized === 'completed' || normalized === 'complete';
}

/**
 * Check if status indicates failure
 */
export function isFailed(status?: string): boolean {
    const normalized = (status || '').toLowerCase();
    return normalized === 'failed' || normalized === 'error';
}

/**
 * Check if status is actively processing
 */
export function isProcessing(status?: string, phase?: string): boolean {
    const normalizedStatus = (status || '').toLowerCase();
    const normalizedPhase = (phase || '').toLowerCase();
    
    const processingStates = [
        'scraping', 'initializing', 'processing',
        'embedding', 'extracting_branding', 'generating_themes'
    ];
    
    return processingStates.some(state => 
        normalizedStatus.includes(state) || normalizedPhase.includes(state)
    );
}

/**
 * Get user-friendly message from status
 */
export function getStatusMessage(currentStatus: ProgressStatus): string {
    // Use provided message if available
    if (currentStatus.message) {
        return currentStatus.message;
    }
    
    const stageInfo = getStageInfo(currentStatus.status, currentStatus.phase);
    
    // Generate message based on status
    if (currentStatus.pages_scraped && currentStatus.total_pages) {
        return `${stageInfo.label}: ${currentStatus.pages_scraped}/${currentStatus.total_pages} pages`;
    }
    
    if (currentStatus.embedding_current && currentStatus.embedding_total) {
        return `Embeddings: ${currentStatus.embedding_current}/${currentStatus.embedding_total} processed`;
    }
    
    return stageInfo.description || stageInfo.label;
}

/**
 * REVAMP: Calculate overall progress percentage
 * Backend now sends single "progress" field (0-100)
 */
export function calculateProgress(currentStatus: ProgressStatus): number {
    // Use backend progress if available (0-100 already calculated server-side)
    if (currentStatus.progress !== undefined && currentStatus.progress !== null) {
        return Math.min(100, Math.max(0, currentStatus.progress));
    }
    
    // Fallback: infer from phase if progress not provided
    const phase = currentStatus.phase || '';
    
    if (phase === 'completed') return 100;
    if (phase === 'applying_themes') return 98;
    if (phase === 'extracting_branding') return 90;
    if (phase === 'generating_embeddings') {
        // Embeddings: 60-85%
        if (currentStatus.embedding_current && currentStatus.embedding_total) {
            return 60 + (currentStatus.embedding_current / currentStatus.embedding_total) * 25;
        }
        return 70;
    }
    if (phase === 'batch_scraping' || phase === 'processing_results') {
        // Scraping: 0-60%
        if (currentStatus.chunks_completed && currentStatus.chunks_total) {
            return (currentStatus.chunks_completed / currentStatus.chunks_total) * 60;
        }
        return 30;
    }
    
    return 5; // Default initializing progress
}

